import React from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { HelperTextWithIcon } from '../HelperTextWithIcon';

enum ERadioButtonSize {
	lg = 'w-4 h-4',
	md = 'w-4 h-4',
	sm = 'w-3.5 h-3.5',
	xs = 'w-3 h-3',
}

enum ERadioButtonFillingSize {
	lg = 'after:w-2.5 after:h-2.5',
	md = 'after:w-2.5 after:h-2.5',
	sm = 'after:w-1.75 after:h-1.75',
	xs = 'after:w-1.5 after:h-1.5',
}

enum ERadioButtonLabelSize {
	lg = 'text-lg md:text-xl',
	md = 'text-lg',
	sm = 'text-base',
	xs = 'text-sm',
}

type RadioButtonSize = keyof typeof ERadioButtonSize;

interface IOption {
	id: string;
	label: string | React.ReactNode;
	title: string;
	disabled?: boolean;
}

type RadioButtonOption = string | string[] | IOption;
type OptionProps = string[] | string[][] | IOption[];

interface IRadioButtonProps {
	className?: string;
	defaultValue?: string;
	hasError?: boolean;
	helperText?: React.ReactNode;
	helperTextStyle?: React.CSSProperties;
	idAlias?: string;
	labelClassName?: string;
	options: OptionProps;
	radioButtonClassName?: string;
	size?: RadioButtonSize;
	value?: string;
	wrapperClassName?: string;
	setValue?: (value: string) => void;
	['data-cy']?: string;
}

const getOptionValues = (option: RadioButtonOption): IOption => {
	if (Array.isArray(option)) {
		return { id: option[0], label: option[1], title: option[2] };
	}
	if (typeof option === 'object') {
		return option;
	}
	return { id: option, label: option, title: option };
};

export const RadioButtons = ({
	className = '',
	defaultValue = undefined,
	hasError,
	helperText = '',
	helperTextStyle,
	idAlias = undefined,
	labelClassName = '',
	options,
	radioButtonClassName = '',
	size = 'lg',
	value = undefined,
	wrapperClassName = '',
	setValue = () => undefined,
	'data-cy': dataCy = undefined,
}: IRadioButtonProps) => (
	<>
		<RadioGroup.Root
			className={`${hasError && 'border-2 border-solid rounded border-error'} ${wrapperClassName}`}
			tabIndex={-1}
			value={value}
			defaultValue={defaultValue}>
			{options.map((option: RadioButtonOption) => {
				const { id, label, title, disabled = false } = getOptionValues(option);
				const textColor = value === id ? 'text-gray-100' : 'text-gray-200';
				return (
					<div
						key={idAlias ? `${idAlias}-${id}` : id}
						className={`flex group focus-visible:outline-primary ${
							disabled ? 'pointer-events-none' : 'cursor-pointer'
						}  ${className}`}
						tabIndex={0}
						role="button"
						aria-label={title}
						aria-disabled={disabled}
						title={title}
						onClick={() => {
							if (id !== value) {
								setValue(id);
							}
						}}
						onKeyDown={(e) => {
							if (e.key !== 'Tab') {
								e.preventDefault();
							}
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								setValue(id);
							}
						}}>
						<RadioGroup.Item
							tabIndex={-1}
							id={idAlias ? `${idAlias}-${id}` : id}
							disabled={disabled}
							value={id}
							title={title}
							data-cy={dataCy ? `${dataCy}-${id}: radio-button` : `radio-button-${id}`}
							className={`cursor-pointer border border-solid flex flex-shrink-0 ${
								disabled
									? 'border-gray-600 pointer-events-none'
									: 'border-gray-350 data-[state=checked]:border-gray-200  hover:border-gray-200 group-hover:border-gray-200'
							} bg-white ${ERadioButtonSize[size]} rounded-full ${radioButtonClassName}`}>
							<RadioGroup.Indicator
								data-cy={`radio-button${id}: indicator`}
								className={`flex items-center justify-center w-full h-full relative after:content-[''] after:block after:rounded-half ${
									ERadioButtonFillingSize[size]
								} ${disabled ? 'after:bg-gray-600 pointer-events-none' : 'after:bg-gray-200'}`}
							/>
						</RadioGroup.Item>
						<label
							data-cy={dataCy ? `${dataCy}-${id}: label` : `radio-button-${id}: label`}
							htmlFor={idAlias ? `${idAlias}-${id}` : id}
							className={`pl-2 ${ERadioButtonLabelSize[size]} ${
								disabled
									? 'text-gray-400 pointer-events-none'
									: `group-hover:cursor-pointer ${textColor}`
							} ${labelClassName}`}>
							{label}
						</label>
					</div>
				);
			})}
		</RadioGroup.Root>
		<HelperTextWithIcon hasError={hasError} helperText={helperText} helperTextStyle={helperTextStyle} />
	</>
);
